"use client";

import { useRef, useState } from "react";
import { PurchaseReviewDialog } from "@/components/local-lessons/PurchaseReviewDialog";
import { CheckoutConsentStep } from "@/components/local-lessons/CheckoutConsentStep";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import { CHECKOUT_FLEXIBLE_PAYMENT_DISCLAIMER } from "@/lib/localLessons/localLessonProductCatalog";
import {
  VOCAL_DISCOVERY_PRODUCT_KEY,
  getPackageLocationLabel,
  type PackageLocation,
} from "@/lib/localLessons/vocalDiscoveryPurchase";
import {
  EMPTY_CHECKOUT_CONSENT,
  validateCheckoutConsentInput,
  type CheckoutConsentInput,
} from "@/lib/consent/bookingConsent";

type VocalDiscoveryPurchaseFlowProps = {
  initialLocation?: PackageLocation;
  onCheckoutRedirect?: (url: string) => void;
};

const TOUCHPOINTS = [
  "Initial private Voice Discovery and goal-setting session online with Liuba — 30 minutes",
  "Private Progress & Voice Blocker Review online with Liuba — 40 minutes",
  "First themed online group session with Liuba",
  "Second themed online group session with Liuba",
  "First personalised Video Feedback check-in",
  "Second personalised Video Feedback check-in",
  "First local Singing Attitude Method exercise session with the confirmed programme coach — 50 minutes",
  "Second local Singing Attitude Method exercise session with the confirmed programme coach — 50 minutes",
] as const;

function createCheckoutRequestId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : "00000000-0000-4000-8000-000000000000";
}

export function VocalDiscoveryPurchaseFlow({
  initialLocation = "either",
  onCheckoutRedirect = (url) => window.location.assign(url),
}: VocalDiscoveryPurchaseFlowProps) {
  const trackEvent = useTrackEvent({ includeAttribution: false });
  const checkoutRequestIdRef = useRef(createCheckoutRequestId());
  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState<PackageLocation>(initialLocation);
  const [error, setError] = useState<string | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [checkoutConsent, setCheckoutConsent] = useState<CheckoutConsentInput>({ ...EMPTY_CHECKOUT_CONSENT });
  const [isSubmitting, setIsSubmitting] = useState(false);

  function analyticsProperties(bookingStage: string) {
    return {
      product_type: "vocal_discovery_package",
      preferred_location: location,
      cta_position: "decision_help",
      device_flow: typeof window !== "undefined" && window.innerWidth < 640 ? "mobile" : "desktop",
      booking_stage: bookingStage,
    };
  }

  function openReview() {
    checkoutRequestIdRef.current = createCheckoutRequestId();
    setOpen(true);
    setError(null);
    setConsentError(null);
    setCheckoutConsent({ ...EMPTY_CHECKOUT_CONSENT });
    trackEvent("vocal_discovery_package_selected", analyticsProperties("package_selected"));
    trackEvent("package_purchase_review_opened", analyticsProperties("purchase_review"));
    trackEvent("flexible_payment_message_viewed", analyticsProperties("purchase_review"));
  }

  function closeReview() {
    if (isSubmitting) return;
    setOpen(false);
    trackEvent("package_purchase_review_closed", analyticsProperties("purchase_review"));
  }

  function changeLocation(nextLocation: PackageLocation) {
    setLocation(nextLocation);
    checkoutRequestIdRef.current = createCheckoutRequestId();
    setError(null);
  }

  async function startCheckout() {
    if (isSubmitting) return;
    try {
      validateCheckoutConsentInput(checkoutConsent);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Please review the consent choices.";
      setConsentError(message);
      if (message === "Please accept the Terms and Conditions to continue.") {
        trackEvent("checkout_terms_validation_failed");
      }
      return;
    }
    setIsSubmitting(true);
    setError(null);
    trackEvent("package_checkout_requested", analyticsProperties("checkout_requested"));
    try {
      const response = await fetch("/api/stripe/vocal-discovery-package/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productKey: VOCAL_DISCOVERY_PRODUCT_KEY,
          preferredLocation: location,
          checkoutRequestId: checkoutRequestIdRef.current,
          consent: checkoutConsent,
        }),
      });
      const data = await response.json() as { url?: string; error?: string };
      if (!response.ok || !data.url) throw new Error(data.error || "Unable to start secure checkout.");
      trackEvent("checkout_continued");
      onCheckoutRedirect(data.url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to start secure checkout.");
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openReview}
        aria-haspopup="dialog"
        aria-label="Book Online + Local Programme"
        className="btn-premium inline-flex min-h-12 w-full items-center justify-center rounded-full border border-[#d9c78f] bg-[#d9c78f] px-5 py-3 text-center text-sm font-semibold text-[#13211f] transition hover:bg-[#f5df9f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#d9c78f]"
      >
        Book
      </button>
      <PurchaseReviewDialog
        open={open}
        onClose={closeReview}
        title="Your Online + Local Programme"
        description="Eight coordinated touchpoints, usually completed over approximately eight weeks."
        footer={(
          <div className="grid gap-2 sm:grid-cols-[auto_1fr]">
            <button type="button" onClick={closeReview} disabled={isSubmitting} className="min-h-12 rounded-[14px] border border-[#d8cab6] px-5 text-sm font-bold text-[#123b34] disabled:opacity-60">
              Close
            </button>
            <button type="button" onClick={startCheckout} disabled={isSubmitting} className="min-h-12 rounded-[14px] bg-[#123b34] px-5 text-sm font-bold text-white disabled:opacity-60">
              {isSubmitting ? "Starting secure checkout…" : "Continue to secure payment — £349"}
            </button>
          </div>
        )}
      >
        <div className="space-y-5">
          <div className="grid gap-3 rounded-[18px] border border-[#d9c78f]/65 bg-[#fff5df] p-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#9b6b19]">Total</p>
              <p className="mt-1 font-serif text-4xl font-semibold text-[#123b34]">£349</p>
              <p className="text-sm font-semibold text-[#53615c]">One-time payment</p>
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#9b6b19]">Timing</p>
              <p className="mt-2 text-lg font-bold text-[#123b34]">Approximately eight weeks</p>
              <p className="text-sm text-[#53615c]">Dates are arranged flexibly with Liuba after your first session.</p>
            </div>
          </div>
          <p className="rounded-[14px] border border-[#e2d3bc] bg-[#fffaf2] p-3 text-xs font-semibold leading-5 text-[#61706b]">
            {CHECKOUT_FLEXIBLE_PAYMENT_DISCLAIMER}
          </p>

          <fieldset>
            <legend className="text-sm font-black text-[#123b34]">Preferred local location</legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              {(["ely", "bury_st_edmunds", "either"] as const).map((value) => (
                <label key={value} className={`flex min-h-12 cursor-pointer items-center gap-2 rounded-[14px] border px-3 text-sm font-bold ${location === value ? "border-[#b7892d] bg-[#fff1cf]" : "border-[#d8cab6] bg-white"}`}>
                  <input type="radio" name="package-location" value={value} checked={location === value} onChange={() => changeLocation(value)} />
                  {getPackageLocationLabel(value)}
                </label>
              ))}
            </div>
          </fieldset>

          <section aria-labelledby="package-touchpoints-title">
            <h3 id="package-touchpoints-title" className="font-serif text-2xl font-semibold text-[#123b34]">Eight purchased touchpoints</h3>
            <ol className="mt-3 grid gap-2 text-sm font-semibold leading-6 text-[#53615c] sm:grid-cols-2">
              {TOUCHPOINTS.map((touchpoint, index) => (
                <li key={touchpoint} className="flex gap-2 rounded-[14px] border border-[#e2d3bc] bg-white p-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#d9c78f] text-xs font-black text-[#123b34]" aria-hidden>{index + 1}</span>
                  <span>{touchpoint}</span>
                </li>
              ))}
            </ol>
          </section>

          <div className="rounded-[18px] border border-[#e2d3bc] bg-white p-4 text-sm leading-6 text-[#53615c]">
            <p className="font-bold text-[#123b34]">Also included</p>
            <p className="mt-1">A personalised high-impact plan, relevant portal resources, flexible scheduling, local coach and studio arrangement, and final progress direction.</p>
            <p className="mt-3 font-semibold text-[#123b34]">Your programme coach, studio and remaining dates will be agreed with Liuba after your first session.</p>
            <p className="mt-2">Your local preference is a request, not a reserved studio or time. If a suitable arrangement cannot be agreed, contact us to discuss alternatives or the cancellation and refund options in the Terms.</p>
          </div>

          <div className="rounded-[18px] border border-[#e2d3bc] bg-white p-4">
            <h3 className="font-bold text-[#123b34]">What happens next</h3>
            <ol className="mt-3 space-y-2 text-sm leading-6 text-[#53615c]">
              <li>1. Continue to secure payment.</li>
              <li>2. Complete the £349 one-time Stripe payment.</li>
              <li>3. Follow the secure next-step link after payment to arrange your private 30-minute opening session online with Liuba. The appointment is confirmed separately.</li>
              <li>4. Discuss your voice, goals and local preference with Liuba.</li>
              <li>5. Receive your connected coaching plan and arrange the remaining online and local touchpoints.</li>
            </ol>
          </div>

          <p className="rounded-[16px] bg-[#10211e] p-4 text-sm font-semibold leading-6 text-white/86">Six Liuba-led touchpoints. Two local exercise sessions. One connected plan.</p>
          <CheckoutConsentStep
            value={checkoutConsent}
            onChange={(next) => { setCheckoutConsent(next); setConsentError(null); }}
            validationError={consentError}
          />
          {error ? <p role="alert" className="rounded-[14px] border border-rose-300 bg-rose-50 p-3 text-sm font-semibold text-rose-900">{error}</p> : null}
        </div>
      </PurchaseReviewDialog>
    </>
  );
}
